'use client';

import { useStore } from '@/stores/store';
import { ADVANCED_SETTINGS_ACTIVATED, DEBUG_INFO_ACTIVATED } from '@/utils/storageKeys';
import { useLocalStorage } from '@mantine/hooks';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { isAdvancedSettingsOpted, isDebugAllowed } from './utils/rules';

export type Feature = 'debug' | 'advancedSettings';
export type FeatureData = Record<Feature, boolean>;

export const FeatureContext = createContext<FeatureData | null>(null);

export function useFeature(feature: Feature): boolean {
  const context = useContext(FeatureContext);

  if (!context) throw new Error('useFeature must be used within a FeatureProvider');

  return context[feature] || false;
}

export const FeatureProvider: React.FC<PropsWithChildren> = (props) => {
  const user = useStore((state) => state.user);

  const [debugInfoActivated] = useLocalStorage<boolean>({
    key: DEBUG_INFO_ACTIVATED,
    defaultValue: false,
  });

  const [advancedSettingsActivated] = useLocalStorage<boolean>({
    key: ADVANCED_SETTINGS_ACTIVATED,
    defaultValue: false,
  });

  const featureData: FeatureData = useMemo(() => {
    const featureData: Record<Feature, boolean> = {
      ['advancedSettings']: isAdvancedSettingsOpted(user, advancedSettingsActivated),
      ['debug']: isDebugAllowed(user, debugInfoActivated),
    };
    return featureData;
  }, [user, debugInfoActivated, advancedSettingsActivated]);

  return <FeatureContext.Provider value={featureData}>{props.children}</FeatureContext.Provider>;
};
